import { useEffect, useState } from 'react';
import { ProxySession, ProxySessionStorageProperties } from './sessions/ProxySession';
import { ManagedSession } from './managed/ManagedSession';
import { Session } from './sessions/Session';
import { MEM_STORAGE, STORAGE, AbstractStorage } from './Storage';
import { SubscriptionContainer } from '../utils/SubscriptionContainer';
import { ManagedStatus } from '@amzn/ring-cs-chatbot-widget';

const LocalStorageSessionStorageProperties = 'ChatBotSessionStorageProperties';

export enum SessionType {
    PROXY = 'PROXY',
}

export type SessionUpdateHandler = (session?: ManagedSession) => void;
export type ManagedStatusHandler = (status: ManagedStatus) => void;

type SessionStorageProperties =
    (ProxySessionStorageProperties & { type: SessionType.PROXY });

namespace SessionStorage {
    type OnChangeHandler = (props?: SessionStorageProperties) => void;

    export const onChange = (storage: AbstractStorage, handler: OnChangeHandler): (() => void) => {
        handler(get(storage));

        return storage.onStorage((key: string) => {
            if (key === LocalStorageSessionStorageProperties) {
                handler(get(storage));
            }
        });
    };

    const get = (storage: AbstractStorage): SessionStorageProperties | undefined => {
        const storagePropertiesString = storage.getItem(LocalStorageSessionStorageProperties);
        const storageProperties: SessionStorageProperties | undefined =
            storagePropertiesString ? JSON.parse(storagePropertiesString) : undefined;

        return storageProperties;
    };

    export const set = (storage: AbstractStorage, props: SessionStorageProperties) => {
        const storagePropertiesString = JSON.stringify(props);
        storage.setItem(LocalStorageSessionStorageProperties, storagePropertiesString);
    };

    export const clear = (storage: AbstractStorage) => {
        storage.removeItem(LocalStorageSessionStorageProperties);
    };

}

type BaseStartSessionProperties = {
    type: SessionType;
    attributes: Record<string, string>;
};

export type StartProxySessionProperties = BaseStartSessionProperties & {
    type: SessionType.PROXY,
    identityId: string;
    token: string;

    sessionId?: string;
};

export type StartSessionProperties = StartProxySessionProperties;

export class SessionManager {
    private readonly sessionUpdateHandlers: Set<SessionUpdateHandler>;
    private readonly statusContainer: SubscriptionContainer<ManagedStatus>;

    private session?: ManagedSession;

    private storage: AbstractStorage;
    private unsubscribeOnChange: () => void;

    constructor () {
        this.sessionUpdateHandlers = new Set();
        this.statusContainer = new SubscriptionContainer<ManagedStatus>(ManagedStatus.NOT_STARTED);

        // set initial storage
        this.storage = STORAGE;
        this.unsubscribeOnChange = SessionStorage.onChange(
            this.storage,
            storageProperties => {
                this.setSessionWithProperties(storageProperties);
            },
        );
    }

    setSessionStorage = (storageType: string) => {
        const storage = {
            memory: MEM_STORAGE,
            localstorage: STORAGE,
        }[storageType];

        // update storage if it is being changed
        if (storage && storage !== this.storage) {
            this.unsubscribeOnChange();
            this.storage = storage;
            this.unsubscribeOnChange = SessionStorage.onChange(
                this.storage,
                storageProperties => {
                    this.setSessionWithProperties(storageProperties);
                },
            );
        }
    };

    startSession = async (properties: StartSessionProperties | Promise<StartSessionProperties>) => {
        // multiple sessions should not start simultaneously
        if (this.statusContainer.value === ManagedStatus.STARTING) {
            throw new Error('Session already starting');
        }

        this.statusContainer.setValue(ManagedStatus.STARTING);
        try {
            const resolved = await properties;

            // update storage type if indicated by clientStorage attribute
            if (resolved.attributes.clientStorage) {
                this.setSessionStorage(resolved.attributes.clientStorage);
            }

            let storageProperties: SessionStorageProperties;
            switch (resolved.type) {
                case SessionType.PROXY:
                    const proxyProperties: ProxySessionStorageProperties = {
                        identityId: resolved.identityId,
                        token: resolved.token,
                        sessionId: resolved.sessionId,
                        attributes: resolved.attributes,
                    };
                    storageProperties = { ...proxyProperties, type: resolved.type };
                    break;
                default:
                    throw new Error(`Unrecognized session type ${(resolved as any).type}`);
            }

            this.setSessionWithProperties(storageProperties);
            SessionStorage.set(this.storage, storageProperties);
        } catch (err) {
            this.setSessionWithProperties(undefined);
            SessionStorage.clear(this.storage);
            throw err;
        }
    };

    private setSessionWithProperties = (properties?: SessionStorageProperties) => {
        if (properties) {
            const type = properties.type;
            switch (type) {
                case SessionType.PROXY:
                    const proxySession = new ProxySession(properties);
                    this.setSession(proxySession);
                    break;
                default:
                    throw new Error(`Unrecognized session type ${type}`);
            }
            this.statusContainer.setValue(ManagedStatus.IN_PROGRESS);
        } else {
            if (this.session) {
                // if a session exists, end it
                this.setSession(undefined);
                this.statusContainer.setValue(ManagedStatus.ENDED);
            } else {
                // no session to end, set as not started
                this.statusContainer.setValue(ManagedStatus.NOT_STARTED);
            }
        }
    };

    private setSession = (session?: Session) => {
        // only set a new session if it has changed
        if (session === this.session) return;

        // force end old session if present
        if (this.session) {
            this.session.clear();
        }

        if (session) {
            // setting a new session, so wrap it to make it managed
            const mSession = new ManagedSession(session);

            mSession.onEnded({
                handler: async () => {
                    // only clear our session if it is still the same one
                    if (this.session === mSession) {
                        this.setSessionWithProperties(undefined);
                        SessionStorage.clear(this.storage);
                    }
                },
            });

            this.session = mSession;
        } else {
            this.session = undefined;
        }

        this.sessionUpdateHandlers.forEach(handler => handler(this.session));
    };

    getSession = () => {
        return this.session;
    };

    onSessionUpdate = (handler: SessionUpdateHandler) => {
        this.sessionUpdateHandlers.add(handler);

        handler(this.session);

        return () => {
            this.sessionUpdateHandlers.delete(handler);
        };
    }

    useSession = () => {
        const [session, setSession] = useState(this.session);

        useEffect(() => {
            return this.onSessionUpdate((session) => {
                setSession(session);
            });
        }, [this]);

        return session;
    }

    useStatus = () => {
        const [status, setStatus] = useState(this.statusContainer.value);

        useEffect(() => {
            return this.statusContainer.onUpdate((status) => {
                setStatus(status);
            });
        }, [this]);

        return status;
    }
}

export const manager = new SessionManager();
